<template>
  <div 
    class="confirmation-container d-flex align-items-center justify-content-center"
    :class="{ 'compact-view': asistenciaConfirmada || showQRCode }"
  >
    <div 
      class="confirmation-card p-4 p-md-5 rounded-4 shadow-lg"
      :class="{ 'compact-card': asistenciaConfirmada || showQRCode }"
      ref="formCardRef"
    >
      <!-- Vista de QR confirmado -->
      <div v-if="asistenciaConfirmada || showQRCode" class="qr-view text-center">
        <p class="qr-header"> Digital Pass</p>
        <div class="qr-wrapper">
          <img v-if="qrCode" :src="qrCode" alt="Código QR" class="qr-code">
          <p v-else class="qr-error-message">The QR code has not been generated.</p>
        </div>
    
        <p class="qr-instructions">Show it on the day of the event</p>
      </div>

      <!-- Formulario paso a paso -->
      <div v-else>
        
        <div class="form-header">
              <h2 ref="tituloAnimado" class="form-title ">Confirm your<br><span class="subtitulo">Attendance </span></h2><br>

          <div class="progress-container">
  <div
    class="progress-bar"
    :style="{ width: ((currentStep - 1) / (totalSteps - 1)) * 100 + '%' }"
  ></div>
</div>
          <p class="form-subtitle">Step {{ currentStep }} of {{ totalSteps }}</p>
        </div>

        <form @submit.prevent="handleSubmit" class="confirmation-form">
          <!-- Paso 1: Nombre y Teléfono -->
          <div v-if="currentStep === 1" class="step-box">
            <div class="form-group">
              <label for="name" class="form-label">Full Name</label>
              <input id="name" v-model.trim="name" type="text" class="form-control" placeholder="Enter your full name" required>
            </div>
            <div class="form-group">
              <label for="phone" class="form-label">Phone</label>
              <input id="phone" v-model.trim="phone" type="tel" class="form-control" placeholder="Enter phone only number" required pattern="[0-9]{10}" title="Enter phone only number 10 digits">
            </div>
          </div>

          <!-- Paso 2: Invitado por (oculto en XV o YM) -->
          <div v-if="currentStep === 2 && !isXvEvent" class="step-box">
            <div class="form-group">
              <label class="form-label">Invited By?</label>
              <div class="radio-group">
                <div class="radio-option">
                  <input id="novia" v-model="invitedBy" type="radio" value="novia" class="radio-input" required>
                  <label for="novia" class="radio-label">Bride</label>
                </div>
                <div class="radio-option">
                  <input id="novio" v-model="invitedBy" type="radio" value="novio" class="radio-input">
                  <label for="novio" class="radio-label">Groom</label>
                </div>
              </div>
            </div>
          </div>

          <!-- Paso 3: Asistencia -->
          <div v-if="currentStep === asistenciaStep" class="step-box">
            <div class="form-group">
              <label class="form-label">You will Attend?</label>
              <div class="radio-group">
                <div class="radio-option">
                  <input id="si" v-model="attending" type="radio" value="si" class="radio-input" required>
                  <label for="si" class="radio-label">Yes</label>
                </div>
                <div class="radio-option">
                  <input id="no" v-model="attending" type="radio" value="no" class="radio-input">
                  <label for="no" class="radio-label">No</label>
                </div>
              </div>
            </div>
          </div>

          <!-- Paso 4: Número de invitados  -->
<div v-if="currentStep === totalSteps && attending !== 'no'" class="step-box">
            <div class="form-group">
              <label for="guests" class="form-label">How many Attend?</label>
              <select id="guests" v-model="guests" class="form-select" :disabled="attending === 'no' || loading" required>
                <option value="" disabled selected>Select an option</option>
                <option v-for="n in guestsOptions" :key="n" :value="n">{{ n }}</option>
                <option v-if="attending === 'no'" value="0">0</option>
              </select>
              <div v-if="loading" class="loading-indicator">Loading options...</div>
            </div>
          </div>

          <!-- Botones de navegación -->
          <div class="d-flex justify-content-between mt-3">
            <button type="button" class="submit-button" @click="prevStep" v-if="currentStep > 1">Back</button>
            <button v-if="currentStep < totalSteps" type="button" class="submit-button" @click="nextStep">Next</button>
            <button v-if="currentStep === totalSteps" type="submit" class="submit-button" :disabled="isSubmitting">
              <span v-if="!isSubmitting">Confirm</span>
              <span v-else class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            </button>
          </div>

          <div v-if="errorMessage" class="alert alert-danger mt-3">{{ errorMessage }}</div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, computed, nextTick } from 'vue';
import axios from 'axios';
import baseUrl from '@/api';
import gsap from 'gsap';
import Swal from 'sweetalert2';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export default {
  name: 'AttendanceConfirmation',
  props: {
    idInvitado: { type: Number, required: true, validator: v => Number.isInteger(v) && v > 0 },
    nombre: { type: String, default: '' },
    idFestejado: { type: Number, default: null }
  },
  setup(props) {
    const name = ref('');
    const phone = ref('');
    const invitedBy = ref('');
    const attending = ref('');
    const guests = ref('');
    const guestsOptions = ref([1, 2]);
    const loading = ref(true);
    const isSubmitting = ref(false);
    const errorMessage = ref('');
    
    const tituloAnimado = ref(null);

    const qrCode = ref(null);
    const showQRCode = ref(false);
    const asistenciaConfirmada = ref(false);
    const currentStep = ref(1);
    const formCardRef = ref(null);

    const isXvEvent = computed(() => {
      const eventName = props.nombre?.toLowerCase();
      return ['xv', 'ym'].includes(eventName);
    });

    const totalSteps = computed(() => isXvEvent.value ? 3 : 4);
    const asistenciaStep = computed(() => isXvEvent.value ? 2 : 3);

  const animateStep = () => {
  nextTick(() => {
    gsap.fromTo(formCardRef.value.querySelector('.step-box'),
      { y: 50, opacity: 0 }, // Comienza 50px más abajo e invisible
      { 
        y: 0,                // Termina en su posición original
        opacity: 1, 
        duration: 0.6, 
        ease: 'power2.out',
        // Opcional: puedes añadir perspectiva para efecto 3D
        transformPerspective: 300,
        rotationX: -10       // Ligera inclinación para efecto de hoja
      });
  });
};
const nextStep = async () => {
  // Validación paso 1: Nombre y teléfono
if (currentStep.value === 1) {
  if (!name.value.trim() || !phone.value.trim()) {
    errorMessage.value = 'Please enter your name and phone only numbers .';
    return;
  
    }
  }

  // Validación paso 2 (si aplica): Invitado por
  if (currentStep.value === 2 && !isXvEvent.value) {
    if (!invitedBy.value) {
errorMessage.value = 'Please select who invited you.';
      return;
    }
  }

  // Validación paso 3: Asistencia
  if (currentStep.value === asistenciaStep.value) {
    if (!attending.value) {
errorMessage.value = 'Please select whether you will attend or not.';
      return;
    }

    // Si dijo que no asiste, saltar a paso final y enviar
    if (attending.value === 'no') {
      currentStep.value = totalSteps.value;
      await nextTick();
      handleSubmit();
      return;
    }
  }

  // Validación paso 4 (si asiste): Cuántos asisten
  if (currentStep.value === totalSteps.value && attending.value === 'si') {
    if (!guests.value || guests.value === '') {
errorMessage.value = 'Please select how many people will attend.';
      return;
    }
  }

  // Si pasa todas las validaciones
  errorMessage.value = '';
  if (currentStep.value < totalSteps.value) {
    currentStep.value++;
    animateStep();
  }
};



    const prevStep = () => {
      if (currentStep.value > 1) {
        currentStep.value--;
        animateStep();
      }
    };

    const fetchGuestLimit = async () => {
      try {
        const response = await axios.get(`${baseUrl}/${props.nombre ? `${props.nombre}/` : ''}${props.idFestejado ? `${props.idFestejado}/` : ''}${props.idInvitado}`);
        const numPases = response.data.num_pase;
        guestsOptions.value = numPases > 0 ? Array.from({ length: numPases }, (_, i) => i + 1) : [1, 2];
      } catch (error) {
        console.error('Error al obtener límite de invitados:', error);
errorMessage.value = 'Error loading guest options.';
      } finally {
        loading.value = false;
      }
    };

    const verificarAsistencia = async () => {
      try {
        const response = await axios.post(`${baseUrl}/api2/verificar-asistencia`, {
          idInvitado: props.idInvitado
        });
        if (response.data.asistio) {
          asistenciaConfirmada.value = true;
          await obtenerCodigoQR();
        }
      } catch (error) {
        console.error('Error al verificar asistencia:', error);
      }
    };

    const obtenerCodigoQR = async () => {
      try {
        const response = await axios.post(`${baseUrl}/api2/codigoqrinvitado`, { id_invitado: Number(props.idInvitado) });
        qrCode.value = response.data?.codigoqr || null;
      } catch (error) {
        console.error('Error obteniendo código QR:', error);
        errorMessage.value = 'Error al generar el código QR';
      }
    };
const handleSubmit = async () => {
  if (attending.value === 'no') guests.value = '0';

  isSubmitting.value = true;
  errorMessage.value = '';

  try {
    const formData = {
      nombre_asistencia: name.value.trim(),
      telefono_invitado: phone.value.trim(),
      invitedby: isXvEvent.value ? 'novia' : invitedBy.value,
      asistira: attending.value,
      id_festejada: props.idFestejado,
      id_invitado: props.idInvitado,
      num_pases: 0,
    };

    await axios.post(`${baseUrl}/asistencia`, formData);

    if (attending.value === 'no') {
      Swal.fire({
        icon: 'info',
        title: '¡Gracias por avisarnos!',
        text: 'Sentimos mucho que no puedas asistir.',
        confirmButtonColor: '#d88cbd',
      });
      asistenciaConfirmada.value = true; // Para ocultar el formulario
    } else {
      await obtenerCodigoQR();
      showQRCode.value = true;
    }
  } catch (error) {
    console.error('Error confirmando asistencia:', error);
    errorMessage.value = 'Hubo un error al confirmar. Por favor intenta nuevamente.';
  } finally {
    isSubmitting.value = false;
  }
};

    watch(attending, (newValue) => {
      if (newValue === 'no') {
        guests.value = '0';
      } else if (newValue === 'si' && guests.value === '0') {
        guests.value = '1';
      }
    });

    onMounted(() => {
      fetchGuestLimit();
      verificarAsistencia();
      animateStep();
    gsap.fromTo(tituloAnimado.value,
  { y: -150, opacity: 0, scale: 1.2 },
  {
    y: 0,
    opacity: 1,
    scale: 1,
    duration: 1.2,
    ease: 'bounce.out',
    scrollTrigger: {
      trigger: tituloAnimado.value,
      start: 'top 80%',
toggleActions: 'restart none none reset'
    }
  }
);

    });

    return {
      name, phone, invitedBy, attending, guests, guestsOptions,
      loading, isSubmitting, errorMessage, isXvEvent, asistenciaConfirmada,
      qrCode, showQRCode, currentStep, nextStep, prevStep, handleSubmit,
      totalSteps, asistenciaStep, formCardRef,  tituloAnimado // <-- ¡FALTABA ESTO!

    };
  }
};
</script>


<style scoped>
/* Estilos base */
.confirmation-container {
 padding: 80px 20px;
    margin: 0 auto;
  border-radius: 0 80px 0 80px; /* 🔸 redondea solo la esquina inferior derecha */
margin-top:20px;
  background-image: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('@/assets/verde.jpg');
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
    width: 90%; /* <-- antes no tenía ancho, ahora 90% */

  background-size: cover;
background-position: center;
background-repeat: no-repeat;

  background-size: cover;
}

/* DISEÑO ESTILO TARJETA INCLINADA RSVP */
.confirmation-card {
  background-image: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('@/assets/verde.jpg');
  border: 3px solid rgba(240, 239, 229, 0.42);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  padding: 2rem;
  max-width: 600px;
  font-family: 'Poppins', sans-serif;
  position: relative;
  z-index: 10;
}

/* Botón verde moderno */
.submit-button {
  background-color: #65696c !important;

  color: #dcd2d2 !important;
  font-weight: bold;
  border-radius: 50px;
  padding: 12px 32px;
  font-family: 'padres';
}

.submit-button:hover {
  background-color: #65696c !important;
}

/* Input minimalistas tipo pizarra */
input.form-control,
select.form-select {
  background-color: transparent;
  border: none;
  border-bottom: 2px solid #eef1e8;
  color: white;
  font-size: 1rem;
}

input.form-control::placeholder {
  color: #cccccc;
}

.form-label {
  color: #ffffff;
  text-transform: uppercase;
  font-weight: bold;
  letter-spacing: 0.5px;
}

/* Fondo general y alineación centrada */
.confirmation-container {
  background-image: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('@/assets/verde.jpg');
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
}

/* Vista compacta para QR */
.compact-view {
  min-height: 70vh;
  align-items: flex-start;
  padding-top: 4rem;
}

.compact-card {
  max-width: 400px;
  padding: 2rem;
}

/* Encabezado del formulario */
.form-header {
  margin-bottom: 2rem;
  text-align: center;
}

.form-title {
  font-family: 'subtitulos' !important;
  font-size: 28px;
  color: #ebeed7;
  margin-bottom: 0.5rem;
  
}

.form-subtitle {
  font-family: 'padres';
  color: #ebeed7;
  font-size: 1rem;
}

/* Grupos de formulario */
.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  font-family: 'padres';
  font-weight: 600;
  color: #e6e9ec;
}

/* Controles de formulario */
.form-control,
.form-select {
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  border: 1px solid #ced4da;
  border-radius: 0.5rem;
  background-color: rgba(255, 255, 255, 0.8);
  transition: border-color 0.3s, box-shadow 0.3s;
}

.form-control:focus,
.form-select:focus {
  border-color: #76787a;
  box-shadow: 0 0 0 0.25rem rgba(163, 160, 163, 0.25);
  outline: none;
}

/* Radio buttons */
.radio-group {
  display: flex;
  gap: 1.5rem;
}

.radio-option {
  display: flex;

  align-items: center;
}

.radio-input {
  margin-right: 0.5rem;
  accent-color: #63676a;
}

.radio-label {
  font-family: 'Karla', sans-serif;
 color: #f6f9fc;
}

/* Botón de enviar */
.submit-button {
  background: #333;
    color: white;
    font-size: 16px;
    font-weight: bold;
    padding: 12px 20px;
    justify-content: center;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease-in-out;
    display: flex;
    align-items: center;
    gap: 8px; /* Espacio entre el ícono y el texto */
    box-shadow: 0px 4px 10px rgba(225, 219, 220, 0.3);
}

.subtitulo {
  font-weight: 500;
  font-size: 26px;
    font-family: 'say2';

}

.submit-button:hover {
  background-color: #333333d1;
}

.submit-button:disabled {
  background-color: #333333c5;
  opacity: 0.7;
  cursor: not-allowed;
}

/* Vista QR */
.qr-view {
  padding: 1rem;
}


.qr-header {
   font-family: 'british', cursive;
  font-size: 24px !important;
  color: #e8eaef;
  ;  font-size: 43px;
  display: block;
  margin-top: 15px;
}

.confirmation-thanks {
  font-family: 'Karla', sans-serif;
  font-weight: 600;
  color: #ebeed7;
  font-size: 1.25rem;
  margin-bottom: 1rem;
}

.qr-wrapper {
  margin: 1rem 0;
}

.qr-code {
  width: 180px;
  height: 180px;
  border: 8px solid white;
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.decoration-crown {
  width: auto;
  height: 80px;
  margin: 0.5rem 0;
}
.radio-input {
  accent-color: #ffd76a;   /* elige un color claro/dorado para resaltar */
}
.event-title {
  font-family: 'Parisienne', cursive;
  font-size: 1.75rem;
  color: #ebeed7;
  margin: 0.5rem 0;
}
.alert-danger {
  background-color: #f0eaec;
  color: #3e3b3c;
  border: 1px solid #f6eff1;
  border-radius: 12px;
  padding: 12px 16px;
  font-family: 'Poppins', sans-serif;
  font-size: 15px;
  text-align: center;
  box-shadow: 0 4px 10px rgba(184, 0, 76, 0.1);
  margin-top: 20px;
}

.qr-instructions {
  font-family: 'Karla', sans-serif;
  color: #d5e4f1;
  font-size: 1rem;
  margin-top: 1rem;
}

/* Mensajes de error */
.alert {
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
}

.qr-error-message {
  color: #dc3545;
  font-weight: 500;
}

.loading-indicator {
  font-size: 0.875rem;
  color: #6c757d;
  margin-top: 0.5rem;
}

/* Responsive */
@media (max-width: 576px) {
  .confirmation-card {
    padding: 1.5rem;
  }
  
  .form-title {
    font-size: 1.75rem;
  }
  
  .radio-group {
    gap: 1rem;
  }
}

.progress-container {
  height: 8px;
  margin-top:40px;
  background-color: #e0e0e0;
  border-radius: 4px;
  margin-bottom: 1rem;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background-color: #83808b; /* color rosado bonito */
  transition: width 0.4s ease;
}

</style>